import { createSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';

import { initialState } from './reducer';
import { INTL_IDS } from './messages';
import {
  HELOC_DRAW_INELIGIBLE,
  HELOC_DRAW_PENDING,
  HELOC_DRAW_PROPS_DICTIONARY,
  MORTGAGE_DETAILS_DICTIONARY,
  NO_TRANSACTIONS_MESSAGE,
} from './constants';
import makeSelectMain, {
  getMainClientInfo,
  selectIsMurabahaAccount,
} from '../Main/selectors';
import {
  capitalizeString,
  convertNumToCurrencyStr,
  formatDate,
  formatPercentage,
  interpolate,
} from '../../utils/globalHelpers';
import { MOBILE_CARD_BUTTON_DICTIONARY } from '../../utils/marketingTilesProps';
import {
  formatEscrowData,
  formatInsuranceData,
  formatLoanTypeDetails,
  formatMortgageInsuranceData,
  formatTaxData,
  getElocBalance,
  getSecondLoanBalance,
} from './helpers';

/**
 * Direct selector to the Mortgage state domain
 */
const selectMortgageDomain = (state) => state.mortgage || initialState;

/**
 * Direct selector to Mortgage props
 * This selector can be used to grab React Router props that maybe passed down
 */
const selectMortgageProps = (state, props) => props;

/**
 * Other specific selectors
 */

const getLoanDetails = () => createSelector(
  makeSelectMain('loanDetails'),
  selectIsMurabahaAccount(),
  (loanDetails, isMurabahaAccount) => {
    const {
      arm,
      constructionLoan,
      eLoc,
      hasEloc,
      hasSecondLoan,
      hasSegmentedEloc,
      isArmLoan,
      isConstructionLoan,
      secondLoan,
      segmentedEloc,
    } = loanDetails;
    const hasOtherDetails = isArmLoan
      || isConstructionLoan
      || hasEloc
      || hasSecondLoan
      || hasSegmentedEloc;
    const eLocBalance = getElocBalance(hasEloc, eLoc);
    const secondLoanBalance = getSecondLoanBalance(hasSecondLoan, secondLoan);

    const armDetails = formatLoanTypeDetails('arm', arm, isArmLoan, isMurabahaAccount);
    const constructionLoanDetails = formatLoanTypeDetails(
      'constructionLoan',
      constructionLoan,
      isConstructionLoan,
    );
    const eLocDetails = formatLoanTypeDetails('eLoc', eLoc, hasEloc, isMurabahaAccount);
    const secondLoanDetails = formatLoanTypeDetails('secondLoan', secondLoan, hasSecondLoan, isMurabahaAccount);
    const segmentedElocDetails = formatLoanTypeDetails(
      'segmentedEloc',
      segmentedEloc,
      hasSegmentedEloc,
      isMurabahaAccount,
    );
    return {
      hasOtherDetails,
      loans: [
        {
          details: armDetails,
          subheader: INTL_IDS.MORTGAGE_DETAILS_SUBHEADER_ARMDETAILS,
          subheaderValue: '',
        },
        {
          details: constructionLoanDetails,
          subheader: INTL_IDS.MORTGAGE_DETAILS_SUBHEADER_CONSTRUCTIONINFORMATION,
          subheaderValue: '',
        },
        {
          details: eLocDetails,
          subheader: INTL_IDS.MORTGAGE_DETAILS_SUBHEADER_HELOCAVAILABLEBALANCE,
          subheaderValue: eLocBalance,
        },
        {
          details: secondLoanDetails,
          subheader: INTL_IDS.MORTGAGE_DETAILS_SUBHEADER_SECONDLOANPRINCIPALBALANCE,
          subheaderValue: secondLoanBalance,
        },
        {
          details: segmentedElocDetails,
          subheader: INTL_IDS.MORTGAGE_DETAILS_SUBHEADER_SEGMENTEDHELOC,
          subheaderValue: '',
        },
      ],
      shouldRenderConstructionLoanDisclaimer: !!constructionLoanDetails,
      shouldRenderHelocDisclaimer: !!eLocDetails,
    };
  },
);

/**
 * @description Selects target error via prop
 */
const makeSelectMortgageError = (prop) => createSelector(
  makeSelectMortgage('errors'),
  (errorState) => errorState[prop],
);

const makeSelectMortgageHomeHeaderView = () => createSelector(
  makeSelectMortgage('isHelocDrawAvailable'),
  selectExpiredPolicies(),
  (isHelocDrawAvailable, expiredPolicies) => {
    if (expiredPolicies.length) {
      return 'expired';
    }
    if (isHelocDrawAvailable) {
      return 'heloc';
    }

    return 'default';
  },
);

/**
 * @description Selects a specified loading state for Mortgage based on the prop
 * @param {string} prop Desired loading state
 */
const makeSelectMortgageLoading = (prop) => createSelector(
  makeSelectMortgage('loading'),
  (loadingState) => loadingState[prop],
);

/**
 * @description Selects a specified service error state based on prop
 */
const makeSelectMortgageServiceError = (prop) => createSelector(
  makeSelectMortgage('errors'),
  ({ serviceError }) => serviceError[prop],
);

/**
* @description selects/formats transactions for FlatList component
*/
const makeSelectFlatListTransactions = () => createSelector(
  makeSelectMortgageLoading('transactions'),
  makeSelectMortgage('transactions'),
  (transactionsLoading, transactions) => {
    const formattedTransactions = transactions.map(({
      amountPaid,
      transactionDate,
      transactionDescription,
      transactionId,
    }) => ({
      amount: convertNumToCurrencyStr(amountPaid),
      date: formatDate(transactionDate, 'UTC', 'll'),
      id: `${transactionId}`,
      isSkeletonData: false,
      type: capitalizeString(transactionDescription),
    }));

    const skeletonTransactions =
      new Array(5).fill(null).map((v, i) => ({
        amount: '',
        date: '',
        id: `${i}`,
        isSkeletonData: true,
        type: '',
      }));

    return transactionsLoading ?
      formattedTransactions.concat(skeletonTransactions) :
      formattedTransactions;
  },
);

/**
 * @description Selects/formats transaction detail
 */
const makeSelectTransactionDetails = () => createSelector(
  makeSelectMortgage('selectedTransaction'),
  (selectedTransaction) => {
    if (!isEmpty(selectedTransaction)) {
      const { amountPaid, transactionDate, transactionDescription } = selectedTransaction;
      return ({
        amountPaid: convertNumToCurrencyStr(Math.abs(amountPaid)),
        transactionDate:
          transactionDate ? formatDate(transactionDate, 'UTC', 'LL') : 'Date Unavailable',
        transactionDescription: transactionDescription || 'Description Unavailable',
      });
    }
    return {};
  },
);

/**
 * @description selects/formats transactions for list component
 */
const makeSelectTransactions = () => createSelector(
  makeSelectMortgage('transactions'),
  (transactions) => transactions.map(({
    amountPaid,
    transactionDate,
    transactionDescription,
    transactionId,
  }) => ({
    amount: convertNumToCurrencyStr(amountPaid),
    date: formatDate(transactionDate, 'UTC', 'll'),
    id: `${transactionId}`,
    isSkeletonData: false,
    type: capitalizeString(transactionDescription),
  })),
);

/**
 * @description Selects errors and formatted form input errors for screen reader
 */
const makeSelectMortgageNotifications = () => createSelector(
  makeSelectMortgageError('bannerError'),
  makeSelectMortgageError('serviceError'),
  makeSelectMortgage('totalPages'),
  makeSelectMortgage('transactions'),
  (
    bannerError,
    {
      escrow: escrowError,
      summary: summaryError,
      transactions: transactionsError,
    },
    totalPages,
    transactions,
  ) => {
    const notifications = [];
    if (bannerError) {
      notifications.push(...bannerError);
    }
    if (escrowError) {
      notifications.push(escrowError);
    }
    if (summaryError) {
      notifications.push(summaryError);
    }
    if (transactionsError) {
      notifications.push(transactionsError);
    }
    if (totalPages !== -1 && !transactions.length) {
      notifications.push(NO_TRANSACTIONS_MESSAGE);
    }
    return notifications;
  },
);

const makeSelectMortgageOverview = () => createSelector(
  makeSelectMortgage('summary'),
  (summary) => {
    if (!isEmpty(summary)) {
      const {
        currentPrincipalBalance,
        effectiveInterestRate,
        loanMaturityDate,
      } = summary;
      return ({
        currentPrincipalBalance: convertNumToCurrencyStr(currentPrincipalBalance),
        effectiveInterestRate: formatPercentage(effectiveInterestRate),
        loanMaturityDate: formatDate(loanMaturityDate, 'UTC', 'M/D/YYYY'),
      });
    }
    return {};
  },
);

const makeSelectTaxesAndInsuranceOverview = () => createSelector(
  makeSelectMortgage('escrow'),
  (escrow) => !isEmpty(escrow) ? formatEscrowData(escrow) : [],
);

const makeSelectMortgageSummary = () => createSelector(
  makeSelectMortgage('summary'),
  (summary) => {
    if (!isEmpty(summary)) {
      const {
        currentPrincipalBalance,
        effectiveInterestRate,
        loanMaturityDate,
        loanOriginationDate,
        loanType,
        originalMortgageAmount,
      } = summary;
      return ({
        currentPrincipalBalance: convertNumToCurrencyStr(currentPrincipalBalance),
        effectiveInterestRate: formatPercentage(effectiveInterestRate),
        loanMaturityDate: formatDate(loanMaturityDate, 'UTC', 'LL'),
        loanOriginationDate: formatDate(loanOriginationDate, 'UTC', 'LL'),
        loanType,
        originalMortgageAmount: convertNumToCurrencyStr(originalMortgageAmount),
      });
    }
    return {};
  },
);

const makeSelectMortgageDetails = () => createSelector(
  makeSelectMortgageSummary(),
  (summary) => Object.keys(summary).reduce((acc, key) => {
    const item = {};
    item.label = MORTGAGE_DETAILS_DICTIONARY[key];
    item.value = summary[key];
    acc.push(item);
    return acc;
  }, []),
);

/**
 * @description selects/formats taxes and insurance details
 */
const makeSelectTaxesAndInsuranceDetails = () => createSelector(
  makeSelectMortgage('escrow'),
  makeSelectMortgage('summary'),
  (escrow, summary) => {
    const {
      escrowSummary,
      insuranceData,
      mortgageInsuranceData,
      taxData,
    } = escrow;
    const {
      currentEscrowBalance,
      currentEscrowPayment,
      effectiveDate,
      lastEscrowAnalysisDate,
      projectedEscrowPayment,
    } = escrowSummary || {};
    const { loanOriginationDate, loanType } = summary;
    const hasEscrow = !!escrowSummary && escrowSummary.currentEscrowPayment > 0;

    return ({
      escrowSummary: {
        currentEscrowBalance:
          currentEscrowBalance ? convertNumToCurrencyStr(currentEscrowBalance) : '',
        currentEscrowPayment:
          currentEscrowPayment ? convertNumToCurrencyStr(currentEscrowPayment) : '',
        effectiveDate:
          effectiveDate ? formatDate(effectiveDate, 'UTC', 'LL') : '',
        lastEscrowAnalysisDate:
          lastEscrowAnalysisDate ? formatDate(lastEscrowAnalysisDate, 'UTC', 'LL') : '',
        projectedEscrowPayment:
          projectedEscrowPayment ? convertNumToCurrencyStr(projectedEscrowPayment) : '',
      },
      hasEscrow,
      insuranceData: insuranceData ? formatInsuranceData(insuranceData) : [],
      loanOriginationDate: loanOriginationDate ? formatDate(loanOriginationDate, 'UTC', 'LL') : '',
      loanType: loanType ? capitalizeString(loanType) : '',
      mortgageInsuranceData:
        mortgageInsuranceData ? formatMortgageInsuranceData(mortgageInsuranceData) : [],
      taxData: taxData ? formatTaxData(taxData) : [],
    });
  },
);

/**
 * @description Selects view
 */
const makeSelectPageView = () => createSelector(
  selectMortgageProps,
  (props) => props.match.params.view || 'main',
);

/**
 * Default selector used by Mortgage
 */
const makeSelectMortgage = (prop) => createSelector(
  selectMortgageDomain,
  (state) => state[prop],
);

const selectAddEscrowOption = () => createSelector(
  makeSelectMortgage('escrow'),
  ({ addEscrowOption }) => addEscrowOption,
);

const selectBannerError = () => createSelector(
  makeSelectMortgage('errors'),
  ({ bannerError }) => bannerError,
);

const selectForm = (prop) => createSelector(
  makeSelectMortgage('forms'),
  (forms) => forms[prop],
);

const selectFormErrors = (prop) => createSelector(
  makeSelectMortgage('formErrors'),
  (formErrors) => formErrors[prop],
);

const selectHasEscrow = () => createSelector(
  makeSelectMortgage('escrow'),
  ({ escrowSummary }) => !!escrowSummary && escrowSummary.currentEscrowPayment > 0,
);

const selectDocumentRequestBody = (documentCategory) => createSelector(
  selectForm('uploadDocument'),
  ({ fileInput, secondFileInput }) => {
    const { fileData, fileName, fileSize } = fileInput;
    const { fileData: fileData2, fileName: fileName2, fileSize: fileSize2 } = secondFileInput;

    return {
      doc2: secondFileInput.fileName ? {
        document: fileData2,
        documentCategory,
        documentName: fileName2,
        documentSize: fileSize2,
      } : null,
      document: fileData,
      documentCategory,
      documentName: fileName,
      documentSize: fileSize,
    };
  },
);

const selectRequestStatus = (prop) => createSelector(
  makeSelectMortgage('requestStatus'),
  (requestStatusState) => requestStatusState[prop],
);

const selectExpiredPolicies = () => createSelector(
  makeSelectMortgage('escrow'),
  ({ insuranceData }) => {
    if (!insuranceData) return [];
    return insuranceData.filter(({ policyExpirationDate }) =>
      moment().isAfter(policyExpirationDate));
  },
);

const selectMobileMarketingCards = () => createSelector(
  selectHasEscrow(),
  makeSelectMortgage('isHelocDrawAvailable'),
  selectIsMurabahaAccount(),
  selectExpiredPolicies(),
  (hasEscrow, isHelocDrawAvailable, isMurabahaAccount, expiredPolicies) => {
    const hasExpiredPolicies = !!expiredPolicies.length;
    const {
      amortization,
      documents,
      escrow,
      glossary,
      helocDraw,
    } = MOBILE_CARD_BUTTON_DICTIONARY;

    const potentialCardsToDisplay = [];

    if (!hasEscrow) {
      potentialCardsToDisplay.push(escrow);
    }
    if (isHelocDrawAvailable && hasExpiredPolicies) {
      potentialCardsToDisplay.push(helocDraw);
    }
    potentialCardsToDisplay.push(
      amortization,
      ...(isMurabahaAccount ? [documents] : [glossary]),
    );

    const finalCardsToDisplay = potentialCardsToDisplay.slice(0, 2);
    return finalCardsToDisplay;
  },
);

// HelocDraw selectors
const selectFormattedHelocDrawDetails = () => createSelector(
  selectHelocDraw(),
  (helocDraw) => {
    const { details } = HELOC_DRAW_PROPS_DICTIONARY;
    return Object.keys(details).map((key) => ({
      label: details[key],
      value: convertNumToCurrencyStr(helocDraw[key]),
    }));
  },
);

const selectHelocDraw = () => createSelector(
  makeSelectMortgage('helocDraw'),
  (helocDraw) => helocDraw,
);

const selectHelocDrawBannerProps = () => createSelector(
  selectHelocDraw(),
  getMainClientInfo('customerServicePhoneNumber'),
  ({ statusBanner }, customerServicePhoneNumber) => {
    const { message, type } = statusBanner;
    let bannerPropsToPassDown = {};

    switch (type) {
      case HELOC_DRAW_INELIGIBLE: {
        bannerPropsToPassDown = {
          message: interpolate(
            message,
            { customerServicePhoneNumber },
          ),
          status: 'ineligible',
        };
        break;
      }
      case HELOC_DRAW_PENDING: {
        bannerPropsToPassDown = {
          message,
          status: 'pending',
        };
        break;
      }
      default: {
        bannerPropsToPassDown = { error: false, success: false };
      }
    }

    return {
      bannerPropsToPassDown,
      shouldRenderStatusBanner: !isEmpty(statusBanner),
    };
  },
);

const selectHelocDrawConfirmationProps = () => createSelector(
  makeSelectMortgage('confirmation'),
  ({ confirmationNumber, requestAmount, requestDate }) => ({
    confirmationConstants: HELOC_DRAW_PROPS_DICTIONARY.confirmation,
    confirmationNumber: confirmationNumber || '',
    requestAmount: convertNumToCurrencyStr(requestAmount),
    requestDate: formatDate(requestDate, 'UTC', 'LL'),
  }),
);

const selectHelocDrawFormProps = () => createSelector(
  makeSelectMortgage('canUseAnyRoutingNumber'),
  makeSelectMortgage('isAccountRequired'),
  selectForm('helocDraw'),
  selectFormErrors('helocDraw'),
  selectHelocDrawBannerProps(),
  selectFormattedHelocDrawDetails(),
  selectMinAndMaxHelocDrawAmounts(),
  getMainClientInfo('clientName'),
  (
    canUseAnyRoutingNumber,
    isAccountRequired,
    formValues,
    formErrors,
    { bannerPropsToPassDown, shouldRenderStatusBanner },
    helocDrawDetails,
    { maxHelocDrawAmount, minHelocDrawAmount },
    clientName,
  ) => (
    {
      bannerPropsToPassDown,
      canUseAnyRoutingNumber,
      clientName,
      formConstants: {
        ...HELOC_DRAW_PROPS_DICTIONARY.form,
        disclaimer: HELOC_DRAW_PROPS_DICTIONARY.form.disclaimer[
          isAccountRequired ? 'cutoffDisclaimer' : 'advanceAmountDisclaimer'],
      },
      formErrors,
      formValues: isAccountRequired ? formValues : { amount: formValues.amount },
      helocDrawDetails,
      isAccountRequired,
      maxHelocDrawAmount,
      minHelocDrawAmount,
      shouldRenderStatusBanner,
    }
  ),
);

const selectHelocDrawInputAmount = () => createSelector(
  selectForm('helocDraw'),
  ({ amount }) => amount || 0,
);

const selectHelocDrawNotifications = () => createSelector(
  selectHelocDraw(),
  makeSelectMortgageError('serviceError'),
  selectFormErrors('helocDraw'),
  getMainClientInfo('customerServicePhoneNumber'),
  (
    { statusBanner },
    { helocDraw: helocDrawError },
    { amount: formError },
    customerServicePhoneNumber,
  ) => {
    const notifications = [];
    if (!isEmpty(statusBanner)) {
      const { message } = statusBanner;
      notifications.push(interpolate(message, { customerServicePhoneNumber }));
    }
    if (helocDrawError) {
      notifications.push(helocDrawError);
    }
    if (formError) {
      notifications.push(formError);
    }

    return notifications;
  },
);

const selectHelocDrawReviewProps = () => createSelector(
  makeSelectMain('selectedLoan'),
  makeSelectMortgage('isAccountRequired'),
  selectForm('helocDraw'),
  selectForm('review'),
  selectHelocDraw(),
  selectRequestStatus('submitHelocDraw'),
  (
    loanNumber,
    isAccountRequired,
    {
      accountNumber,
      accountType,
      amount,
      nameOnAccount,
    },
    { isHelocDrawConfirmChecked },
    { routingNumber },
    status,
  ) => ({
    accountNumber,
    accountType,
    amount: amount || 0,
    formattedAmount: convertNumToCurrencyStr(amount),
    isAccountRequired,
    isHelocDrawConfirmChecked,
    loanNumber,
    maskedAccountNumber: isAccountRequired ? `...${accountNumber.slice(-4)}` : '',
    nameOnAccount,
    requestDate: formatDate(Date.now(), 'UTC', 'LL'),
    reviewConstants: HELOC_DRAW_PROPS_DICTIONARY.review,
    routingNumber,
    status,
  }),
);

const selectIsInHelocDrawFlow = () => createSelector(
  selectHelocDraw(),
  ({ isInHelocDrawFlow }) => isInHelocDrawFlow,
);

const selectMinAndMaxHelocDrawAmounts = () => createSelector(
  selectHelocDraw(),
  ({ availableAdvanceAmount, minimumAdvanceAmount }) => ({
    maxHelocDrawAmount: availableAdvanceAmount || 0,
    minHelocDrawAmount: minimumAdvanceAmount || 0,
  }),
);

export default makeSelectMortgage;
export {
  getLoanDetails,
  makeSelectFlatListTransactions,
  makeSelectMortgage,
  makeSelectMortgageDetails,
  makeSelectMortgageError,
  makeSelectMortgageHomeHeaderView,
  makeSelectMortgageLoading,
  makeSelectMortgageNotifications,
  makeSelectMortgageOverview,
  makeSelectMortgageServiceError,
  makeSelectMortgageSummary,
  makeSelectPageView,
  makeSelectTaxesAndInsuranceDetails,
  makeSelectTaxesAndInsuranceOverview,
  makeSelectTransactionDetails,
  makeSelectTransactions,
  selectAddEscrowOption,
  selectBannerError,
  selectDocumentRequestBody,
  selectExpiredPolicies,
  selectForm,
  selectFormErrors,
  selectHasEscrow,
  selectHelocDraw,
  selectHelocDrawConfirmationProps,
  selectHelocDrawFormProps,
  selectHelocDrawInputAmount,
  selectHelocDrawNotifications,
  selectHelocDrawReviewProps,
  selectIsInHelocDrawFlow,
  selectMobileMarketingCards,
  selectMortgageDomain,
  selectRequestStatus,
};
